import { cn } from "../lib/utils";
import { cva } from "class-variance-authority";
import * as React from "react";
const progressBarVariants = cva("h-1.5 overflow-hidden", {
    variants: {
        radius: {
            none: "rounded-none",
            xs: "rounded-xs",
            full: "rounded-full",
        },
        variant: {
            default: "bg-muted-background",
        },
    },
    defaultVariants: {
        radius: "full",
        variant: "default",
    },
});
const progressBarFillVariants = cva("h-full", {
    variants: {
        radius: {
            none: "rounded-none",
            xs: "rounded-xs",
            full: "rounded-full",
        },
        variant: {
            default: "bg-primary-light",
        },
    },
    defaultVariants: {
        radius: "full",
        variant: "default",
    },
});
/**
 * A slim determinate progress indicator for a known completion percentage.
 * Exposes the `progressbar` role with value semantics; pass `label` to name
 * what is progressing for screen readers.
 *
 * For indeterminate waits with no percentage, use Spinner instead.
 *
 * @summary Determinate progress indicator (0-100%).
 */
export const ProgressBar = React.forwardRef(({ percentage, values, label = "Progress", radius = "full", variant = "default", className, ...props }, ref) => {
    var _a;
    var _b;
    const isSegmented = values !== undefined;
    const nonNegativeValues = (_b = values === null || values === void 0 ? void 0 : values.map((item) => ({
        ...item,
        value: Math.max(0, item.value),
    }))) !== null && _b !== void 0 ? _b : [];
    const totalValue = nonNegativeValues.reduce((total, item) => total + item.value, 0);
    const normalizedValues = isSegmented
        ? nonNegativeValues.map((item) => ({
            percentage: totalValue > 0 ? (item.value / totalValue) * 100 : 0,
            className: item.className,
        }))
        : [
            {
                percentage: Math.min(100, Math.max(0, percentage !== null && percentage !== void 0 ? percentage : 0)),
                className: undefined,
            },
        ];
    return (React.createElement("div", { ref: ref, role: "progressbar", "aria-label": label, "aria-valuenow": (_a = normalizedValues[0]) === null || _a === void 0 ? void 0 : _a.percentage, "aria-valuemin": 0, "aria-valuemax": 100, className: cn(progressBarVariants({ radius, variant }), isSegmented && "flex gap-0.5", className), ...props }, normalizedValues.map((segment, index) => !isSegmented || segment.percentage > 0 ? (React.createElement("div", { key: index, className: cn(progressBarFillVariants({ radius, variant }), segment.className), style: isSegmented
            ? { flexBasis: 0, flexGrow: segment.percentage }
            : { width: `${segment.percentage}%` } })) : null)));
});
ProgressBar.displayName = "ProgressBar";
//# sourceMappingURL=ProgressBar.js.map